import Swal from "sweetalert2";
import { loadFromStorage, saveToStorage } from "@/utils/storage";

const seenReportsKey = (userId) => `generatedReports:seen:${userId || "anon"}`;

export default {
  namespaced: true,
  state: {
    payrollData: [],
    paymentData: [],
    maxHoursData: [],
    maxHoursPeriods: [],
    isLoading: false,
    isSaving: false,
    payrollPagination: {
      current: null,
      total: null,
    },

    // Report generation - stores the format being generated (excel/pdf/csv) or null
    reportGenerating: null,
    generatedReports: {
      loading: false,
      data: [],
      pagination: {
        currentPage: 1,
        totalPages: 1,
        total: 0,
      },
    },

    // Seen report ids, mirrored to localStorage per user (see actions below).
    seenGeneratedReportIds: [],
    // Accumulated unread ids split by status — grow as polls detect new reports,
    // cleared when the drawer is opened. Badge count and color are derived from
    // these, not from the paginated generatedReports.data.
    unreadCompletedReportIds: [],
    unreadFailedReportIds: [],

    // Pre-aggregated billing report preview (GET billing-preview)
    billingPreview: {
      loading: false,
      rows: [],
      grandTotals: {},
      count: 0,
    },
  },
  mutations: {
    SET_STATE: (state, obj) => {
      state[`${obj.key}`] = obj.value;
    },
    SET_PAYROLL_PAGINATION: (state, value) => {
      state.payrollPagination = value;
    },
    SET_PAYROLL_CURRENT_PAGE: (state, value) => {
      state.payrollPagination.current = value;
    },

    // Report generation mutations
    SET_REPORT_GENERATING: function (state, payload) {
      state.reportGenerating = payload;
    },
    SET_GENERATED_REPORTS_LOADING: function (state, payload) {
      state.generatedReports.loading = payload;
    },
    SET_GENERATED_REPORTS_DATA: function (state, payload) {
      state.generatedReports.data = payload;
    },
    SET_GENERATED_REPORTS_PAGINATION: function (state, payload) {
      state.generatedReports.pagination = payload;
    },
    SET_SEEN_GENERATED_REPORT_IDS: function (state, payload) {
      state.seenGeneratedReportIds = payload;
    },
    SET_UNREAD_COMPLETED_REPORT_IDS: function (state, payload) {
      state.unreadCompletedReportIds = payload;
    },
    SET_UNREAD_FAILED_REPORT_IDS: function (state, payload) {
      state.unreadFailedReportIds = payload;
    },
    SET_BILLING_PREVIEW_LOADING: function (state, payload) {
      state.billingPreview.loading = payload;
    },
    SET_BILLING_PREVIEW_DATA: function (state, payload) {
      state.billingPreview.rows = payload.rows;
      state.billingPreview.grandTotals = payload.grandTotals;
      state.billingPreview.count = payload.count;
    },
  },
  actions: {
    getPayments: function (context, id) {
      context.commit("SET_STATE", { key: "isLoading", value: true });
      if (typeof id == "undefined") {
        this.$http
          .get("") //TODO change for payrolls
          .then((res) => {
            if (res.status == 200) {
              context.commit("SET_STATE", {
                key: "paymentData",
                value: res.data.data,
              });
            }
            context.commit("SET_STATE", { key: "isLoading", value: false });
          })
          .catch((err) => {
            context.commit("SET_STATE", { key: "isLoading", value: false });
            if (!err.accessDenied) {
              Swal.fire({
                title: "Error",
                text: (err.data || {}).message || "Something went wrong...",
                icon: "error",
              });
            }
          });
      } else {
        this.$http
          .get(`providers/${id}`)
          .then((res) => {
            context.commit("SET_STATE", {
              key: "billingData",
              value: res.data.data,
            });
            context.commit("SET_STATE", { key: "isLoading", value: false });
          })
          .catch((err) => {
            context.commit("SET_STATE", { key: "isLoading", value: false });
            if (!err.accessDenied) {
              Swal.fire({
                title: "Error",
                text: (err.data || {}).message || "Something went wrong...",
                icon: "error",
              });
            }
          });
      }
    },
    getPayrollSpecificDates: async function ({ commit, state }, payload = {}) {
      if (!payload.page) {
        commit("SET_STATE", { key: "isLoading", value: true });
      }
      let vm = this;
      let url =
        "payroll-invoices-items" +
        `?start=${payload.start}&end=${payload.end}` +
        (payload.providerId ? `&providerId=${payload.providerId}` : ``) +
        (payload.providerIds ? `&providerIds=${payload.providerIds}` : ``) +
        (payload.clientId ? `&clientId=${payload.clientId}` : ``) +
        (payload.classes ? `&classes=${payload.classes}` : ``) +
        (payload.clinics ? `&clinics=${payload.clinics}` : ``) +
        (payload.services ? `&services=${payload.services}` : ``) +
        (payload.admin ? `&admin=${payload.admin}` : ``) +
        (payload.adminIds ? `&adminIds=${payload.adminIds}` : ``) +
        (payload.sessionStatus != "" && payload.sessionStatus != undefined
          ? `&sessionStatus=${payload.sessionStatus}`
          : ``) +
        `&billable=${payload.billable}`;
      if (payload.page) {
        url = `${url}&page=${payload.page}`;
      }
      if (payload.limit) {
        url = `${url}&limit=${payload.limit}`;
      }
      return new Promise((resolve) => {
        vm.$http
          .get(url)
          .then((res) => {
            if (res.status == 200) {
              if (payload.page || payload.limit) {
                commit("SET_PAYROLL_PAGINATION", {
                  total: res.data.meta.total,
                  current: res.data.meta.current_page,
                });
                commit("SET_STATE", {
                  key: "payrollData",
                  value: payload.page
                    ? [...state.payrollData, ...res.data.data]
                    : res.data.data,
                });
              } else {
                commit("SET_PAYROLL_PAGINATION", {
                  total: res.data.data.length,
                  current: 1,
                });
                commit("SET_STATE", {
                  key: "payrollData",
                  value: res.data.data,
                });
              }
            }
            commit("SET_STATE", { key: "isLoading", value: false });
            return resolve(res.data.data);
          })
          .catch((err) => {
            commit("SET_STATE", { key: "isLoading", value: false });
            if (!err.accessDenied) {
              Swal.fire({
                title: "Error",
                text: (err.data || {}).message || "Something went wrong...",
                icon: "error",
              });
              return resolve();
            }
          });
      });
    },
    getMaxhours: function (context, payload) {
      context.commit("SET_STATE", { key: "isLoading", value: true });
      let url = `client-maxhours-report`;
      const vm = this;
      if (payload) {
        if (payload.beginDateIntervals) {
          url = `${url}?beginDateIntervals=${payload.beginDateIntervals}`;
        }
        if (payload.endDateIntervals) {
          url = `${url}&endDateIntervals=${payload.endDateIntervals}`;
        }
        if (payload.period_type) {
          url = `${url}&period_type=${payload.period_type}`;
        }
        if (payload.client_id) {
          url = `${url}&client_id=${payload.client_id}&`;
        }
        if (payload.provider_id) {
          url = `${url}&provider_id=${payload.provider_id}&`;
        }
      }
      return new Promise((resolve) => {
        vm.$http
          .get(url)
          .then((res) => {
            if (res.status == 200) {
              context.commit("SET_STATE", {
                key: "maxHoursData",
                value: res.data.data,
              });
              context.commit("SET_STATE", {
                key: "maxHoursPeriods",
                value: res.data.periods,
              });
            }
            context.commit("SET_STATE", { key: "isLoading", value: false });
            return resolve(res.data);
          })
          .catch((err) => {
            context.commit("SET_STATE", { key: "isLoading", value: false });
            if (!err.accessDenied) {
              Swal.fire({
                title: "Error",
                text:
                  (err.data || err.response?.data || {}).message ||
                  "Something went wrong...",
                icon: "error",
              });
            }
            return resolve();
          });
      });
    },
    getServices: function () {
      let vm = this;
      return new Promise((resolve) => {
        vm.$http
          .get("billing-services")
          .then((res) => {
            return resolve(res.data.data);
          })
          .catch((err) => {
            if (!err.accessDenied) {
              Swal.fire({
                title: "Error",
                text: (err.data || {}).message || "Something went wrong...",
                icon: "error",
              });
              return resolve();
            }
          });
      });
    },
    setPayrollCurrentPage: function ({ commit }, current_page) {
      commit("SET_PAYROLL_CURRENT_PAGE", current_page);
    },
    generateHcaiLineItems: function (_, payload) {
      return new Promise((resolve) => {
        this.$http
          .post("generate-hcai-line-items", payload)
          .then((res) => {
            if (res.status == 200 || res.status == 207) {
              const data = res.data || {};
              return resolve({
                success: true,
                partial: res.status == 207,
                message: data.message || "",
                created_count: data.created_count || 0,
                skipped_count: data.skipped_count || 0,
                errors: data.errors || [],
              });
            }
            return resolve({ success: false });
          })
          .catch((err) => {
            // Keep this action "silent" so caller views can render full issue lists.
            return resolve({
              success: false,
              message:
                (err.data || err.response?.data || {}).message ||
                "Something went wrong...",
              created_count:
                (err.data || err.response?.data || {}).created_count || 0,
              skipped_count:
                (err.data || err.response?.data || {}).skipped_count || 0,
              errors: (err.data || err.response?.data || {}).errors || [],
            });
          });
      });
    },

    getBillingPreview: function ({ commit }, payload = {}) {
      const vm = this;
      commit("SET_BILLING_PREVIEW_LOADING", true);

      const params = [];
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.push(`${key}=${encodeURIComponent(value)}`);
        }
      });
      const url = `billing-preview${
        params.length ? "?" + params.join("&") : ""
      }`;

      return new Promise((resolve) => {
        return vm.$http.get(url).then(
          (res) => {
            commit("SET_BILLING_PREVIEW_DATA", {
              rows: res.data.data || [],
              grandTotals: res.data.grandTotals || {},
              count: (res.data.meta || {}).count || 0,
            });
            commit("SET_BILLING_PREVIEW_LOADING", false);
            resolve(res.data);
          },
          (err) => {
            commit("SET_BILLING_PREVIEW_LOADING", false);
            if (!err.accessDenied) {
              Swal.fire({
                title: "Error",
                text: (err.data || {}).message || "Something went wrong...",
                icon: "error",
              });
            }
            resolve(false);
          }
        );
      });
    },

    generateReport: function ({ commit }, payload) {
      const vm = this;
      commit("SET_REPORT_GENERATING", payload.format);

      return new Promise((resolve) => {
        return vm.$http.post("reports/generate", payload).then(
          (res) => {
            commit("SET_REPORT_GENERATING", null);
            resolve(res.data);
          },
          (err) => {
            commit("SET_REPORT_GENERATING", null);
            if (!err.accessDenied) {
              Swal.fire({
                title: "Error",
                text:
                  err?.data?.error?.message ||
                  err?.data?.message ||
                  "Failed to generate report",
                icon: "error",
              });
            }
            resolve(false);
          }
        );
      });
    },

    getGeneratedReports: function ({ commit }, payload = {}) {
      const vm = this;
      if (!payload.silent) {
        commit("SET_GENERATED_REPORTS_LOADING", true);
      }

      const filters = [];
      if (payload.page) filters.push(`page=${payload.page}`);
      if (payload.limit) filters.push(`limit=${payload.limit}`);
      if (payload.type) filters.push(`type=${payload.type}`);

      const url = `reports${filters.length ? "?" + filters.join("&") : ""}`;

      return new Promise((resolve) => {
        return vm.$http.get(url).then(
          (res) => {
            commit("SET_GENERATED_REPORTS_DATA", res.data.data);
            const meta = res.data.meta || res.data;
            commit("SET_GENERATED_REPORTS_PAGINATION", {
              currentPage: meta.current_page || 1,
              totalPages: meta.last_page || 1,
              total: meta.total || 0,
            });
            if (!payload.silent) {
              commit("SET_GENERATED_REPORTS_LOADING", false);
            }
            resolve(res.data.data);
          },
          (err) => {
            if (!payload.silent) {
              commit("SET_GENERATED_REPORTS_LOADING", false);
            }
            if (payload.silent) {
              console.warn("Generated reports silent fetch failed", err);
            } else if (!err.accessDenied) {
              Swal.fire({
                title: "Error",
                text: err?.data?.error?.message || "Failed to load reports",
                icon: "error",
              });
            }
            resolve(false);
          }
        );
      });
    },

    deleteGeneratedReport: function ({ commit, state }, reportId) {
      const vm = this;

      return new Promise((resolve) => {
        return vm.$http.delete(`reports/${reportId}`).then(
          () => {
            const updatedData = state.generatedReports.data.filter(
              (r) => r.id !== reportId
            );
            commit("SET_GENERATED_REPORTS_DATA", updatedData);
            resolve(true);
          },
          (err) => {
            if (!err.accessDenied) {
              Swal.fire({
                title: "Error",
                text: err?.data?.message || "Failed to delete report",
                icon: "error",
              });
            }
            resolve(false);
          }
        );
      });
    },

    loadSeenGeneratedReportIds: function ({ commit, rootState }) {
      const userId = rootState.auth?.user?.id;
      if (!userId) {
        // Auth not resolved yet — watcher in BillingReport.vue will retry once it is.
        return false;
      }
      const stored = loadFromStorage(seenReportsKey(userId), null);
      const isFirstUse = stored === null;
      if (isFirstUse) {
        // Stamp the key so subsequent loads don't re-trigger seeding.
        saveToStorage(seenReportsKey(userId), []);
      }
      commit(
        "SET_SEEN_GENERATED_REPORT_IDS",
        isFirstUse ? [] : Array.isArray(stored) ? stored : []
      );
      return isFirstUse;
    },

    markGeneratedReportsSeen: function (
      { commit, state, rootState },
      ids = []
    ) {
      if (!Array.isArray(ids) || ids.length === 0) {
        return;
      }
      const SEEN_LIMIT = 500;
      const merged = Array.from(
        new Set([...state.seenGeneratedReportIds, ...ids])
      ).slice(-SEEN_LIMIT);

      commit("SET_SEEN_GENERATED_REPORT_IDS", merged);
      // Drawer is opening — user has acknowledged all pending unread reports.
      commit("SET_UNREAD_COMPLETED_REPORT_IDS", []);
      commit("SET_UNREAD_FAILED_REPORT_IDS", []);
      const userId = rootState.auth?.user?.id;
      saveToStorage(seenReportsKey(userId), merged);
    },

    mergeUnreadReportIds: function ({ commit, state }, reports = []) {
      const seenSet = new Set(state.seenGeneratedReportIds);
      const newCompleted = [];
      const newFailed = [];

      for (const r of reports) {
        if (seenSet.has(r.id)) continue;
        if (r.status === "COMPLETED") newCompleted.push(r.id);
        else if (r.status === "FAILED") newFailed.push(r.id);
      }

      if (newCompleted.length > 0) {
        commit(
          "SET_UNREAD_COMPLETED_REPORT_IDS",
          Array.from(
            new Set([...state.unreadCompletedReportIds, ...newCompleted])
          )
        );
      }
      if (newFailed.length > 0) {
        commit(
          "SET_UNREAD_FAILED_REPORT_IDS",
          Array.from(new Set([...state.unreadFailedReportIds, ...newFailed]))
        );
      }
    },
  },
};
